const KEYS = {
  token: "token",
  username: "username",
  grupo: "grupo",
  isSuperuser: "is_superuser",
  nivel: "nivel",
  roles: "roles",
  permissions: "permissions",
  authMode: "auth_mode",
  authenticated: "authenticated",
};

const PERSISTENCE_KEY = "mps_persistent_session";
const COOKIE_CONSENT_KEY = "mps_cookie_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

function getCookie(name) {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=") || "";
}

function setCookie(name, value, maxAge = COOKIE_MAX_AGE) {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function canUsePersistentSession() {
  return localStorage.getItem(PERSISTENCE_KEY) === "true" || decodeURIComponent(getCookie(COOKIE_CONSENT_KEY)) === "accepted";
}

function readStoredValue(key) {
  const sessionValue = sessionStorage.getItem(key);
  if (sessionValue !== null && sessionValue !== undefined) return sessionValue;
  return canUsePersistentSession() ? localStorage.getItem(key) : null;
}

function parseList(key) {
  try {
    const value = readStoredValue(key);
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export function getSessionValue(key, fallback = "") {
  return readStoredValue(key) || fallback;
}

export function getSession() {
  return {
    token: getSessionValue(KEYS.token),
    username: getSessionValue(KEYS.username),
    grupo: getSessionValue(KEYS.grupo),
    roles: parseList(KEYS.roles),
    permissions: parseList(KEYS.permissions),
    nivel: getSessionValue(KEYS.nivel),
    authMode: getSessionValue(KEYS.authMode),
    isSuperuser: getSessionValue(KEYS.isSuperuser) === "true",
    authenticated: getSessionValue(KEYS.authenticated) === "true",
  };
}

export function hasSession() {
  const session = getSession();
  return Boolean(session.token || session.authenticated);
}

export function hasRole(allowedRoles = []) {
  if (!allowedRoles.length) return true;
  const session = getSession();
  if (session.isSuperuser) return true;
  const roles = session.roles.length ? session.roles : [session.grupo].filter(Boolean);
  return allowedRoles.some((role) => roles.includes(role));
}

export function hasPermission(allowedPermissions = []) {
  if (!allowedPermissions.length) return true;
  const session = getSession();
  if (session.isSuperuser) return true;
  return allowedPermissions.some((permission) => session.permissions.includes(permission));
}

export function clearSession() {
  Object.values(KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  localStorage.removeItem(PERSISTENCE_KEY);
  deleteCookie(COOKIE_CONSENT_KEY);
  localStorage.setItem("log", "false");
}

export function saveSession({ token, username, grupo, roles = [], permissions = [], isSuperuser, authMode, remember = false }) {
  const storage = remember ? localStorage : sessionStorage;
  const otherStorage = remember ? sessionStorage : localStorage;

  Object.values(KEYS).forEach((key) => otherStorage.removeItem(key));

  storage.setItem(KEYS.token, token || "");
  storage.setItem(KEYS.username, username || "");
  storage.setItem(KEYS.grupo, grupo || "");
  storage.setItem(KEYS.roles, JSON.stringify(Array.isArray(roles) ? roles : []));
  storage.setItem(KEYS.permissions, JSON.stringify(Array.isArray(permissions) ? permissions : []));
  storage.setItem(KEYS.nivel, grupo === "Operario" ? "operador" : "contabilidade");
  storage.setItem(KEYS.isSuperuser, isSuperuser ? "true" : "false");
  storage.setItem(KEYS.authMode, authMode || (token ? "token" : ""));
  storage.setItem(KEYS.authenticated, token || authMode === "cookie" ? "true" : "false");

  if (remember) {
    localStorage.setItem(PERSISTENCE_KEY, "true");
    setCookie(COOKIE_CONSENT_KEY, "accepted");
  } else {
    localStorage.removeItem(PERSISTENCE_KEY);
    deleteCookie(COOKIE_CONSENT_KEY);
  }

  localStorage.setItem("log", token || authMode === "cookie" ? "true" : "false");
}
